import React, { useState, useEffect, useMemo, useRef } from 'react';
import Map from './components/Map';
import DiseaseChart from './components/DiseaseChart';
import { getMunicipios, getUfMalha, getMunicipioMalha, getDengueData, getZikaData, getChikungunyaData, getMunicipioInfo, getPopulationData, getMunicipioMetadata, getCnesPorNatureza, getCnesEquipes } from './services/ibgeService';
import { Municipio, GeoJson } from './types/ibge';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { PiHospitalFill } from "react-icons/pi";
import { FaUserDoctor } from "react-icons/fa6";
import './App.css';
import Logo from './assets/images/logo-nome.svg';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

const processData = (rawData: any[], years: number[], municipioId?: string) => {
  const weeklyData: { [year: number]: number[] } = {};
  years.forEach(year => {
    weeklyData[year] = Array(52).fill(0);
  });

  const allMunicipalities = rawData.map(dataForYear => {
    const municipalitiesFromApi = Object.values(dataForYear);
    return municipalitiesFromApi.map((mun: any) => ({ ...mun, year: dataForYear.year }));
  }).flat();

  const dataToProcess = municipioId
    ? allMunicipalities.filter(mun => mun.cod_municipio === municipioId.slice(0, -1))
    : allMunicipalities;

  dataToProcess.forEach((entry: any) => {
    if (entry && entry.dados_semanais) {
      entry.dados_semanais.forEach((semana: any) => {
        const weekNumber = parseInt(semana.semana.replace('Semana ', ''));
        if (!isNaN(weekNumber) && weekNumber >= 1 && weekNumber <= 52) {
          weeklyData[entry.year][weekNumber - 1] += semana.casos;
        }
      });
    }
  });

  return {
    labels: Array.from({ length: 52 }, (_, i) => `S${i + 1}`),
    datasets: years.map((year, index) => ({
      label: year.toString(),
      data: weeklyData[year],
      borderColor: `hsl(${index * 60}, 70%, 50%)`,
      fill: false,
    })),
  };
};

function App() {
  const [ufs, setUfs] = useState<{ value: string; label: string }[]>([]);
  const [uf, setUf] = useState<{ value: string; label: string } | null>(null);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [municipio, setMunicipio] = useState<Municipio | null>(null);
  const [zoning, setZoning] = useState<number>(0);
  const [ufMalha, setUfMalha] = useState<GeoJson | null>(null);
  const [municipioMalha, setMunicipioMalha] = useState<GeoJson | null>(null);
  const [cnesPorNaturezaData, setCnesPorNaturezaData] = useState<any[]>([]);
  const [cnesEquipesData, setCnesEquipesData] = useState<any[]>([]);
  const [municipioInfo, setMunicipioInfo] = useState<any | null>(null);
  const [populationData, setPopulationData] = useState<any | null>(null);
  const [municipioMetadata, setMunicipioMetadata] = useState<any | null>(null);

  const [rawDengueData, setRawDengueData] = useState<any[]>([]);
  const [rawZikaData, setRawZikaData] = useState<any[]>([]);
  const [rawChikungunyaData, setRawChikungunyaData] = useState<any[]>([]);

  const [dengueLoading, setDengueLoading] = useState<boolean>(false);
  const [zikaLoading, setZikaLoading] = useState<boolean>(false);
  const [chikungunyaLoading, setChikungunyaLoading] = useState<boolean>(false);
  const [cnesLoading, setCnesLoading] = useState<boolean>(false);

  const [displayCnesPorNaturezaDialog, setDisplayCnesPorNaturezaDialog] = useState<boolean>(false);
  const [displayCnesEquipesDialog, setDisplayCnesEquipesDialog] = useState<boolean>(false);


  const zoningOptions = [
    { label: '1 km²', value: 1000000 },
    { label: '10 km²', value: 10000000 },
    { label: '100 km²', value: 100000000 },
  ];

  useEffect(() => {
    const ufsList = [
      { value: 'AC', label: 'Acre' }, { value: 'AL', label: 'Alagoas' }, { value: 'AP', label: 'Amapá' },
      { value: 'AM', label: 'Amazonas' }, { value: 'BA', label: 'Bahia' }, { value: 'CE', label: 'Ceará' },
      { value: 'DF', label: 'Distrito Federal' }, { value: 'ES', label: 'Espírito Santo' }, { value: 'GO', label: 'Goiás' },
      { value: 'MA', label: 'Maranhão' }, { value: 'MT', label: 'Mato Grosso' }, { value: 'MS', label: 'Mato Grosso do Sul' },
      { value: 'MG', label: 'Minas Gerais' }, { value: 'PA', label: 'Pará' }, { value: 'PB', label: 'Paraíba' },
      { value: 'PR', label: 'Paraná' }, { value: 'PE', label: 'Pernambuco' }, { value: 'PI', label: 'Piauí' },
      { value: 'RJ', label: 'Rio de Janeiro' }, { value: 'RN', label: 'Rio Grande do Norte' }, { value: 'RS', label: 'Rio Grande do Sul' },
      { value: 'RO', label: 'Rondônia' }, { value: 'RR', label: 'Roraima' }, { value: 'SC', label: 'Santa Catarina' },
      { value: 'SP', label: 'São Paulo' }, { value: 'SE', label: 'Sergipe' }, { value: 'TO', label: 'Tocantins' },
    ];
    setUfs(ufsList);
  }, []);

  const selectUF = async (uf: any) => {
    if (uf) {
      getMunicipios(uf.value).then(setMunicipios);
      getUfMalha(uf.value).then(setUfMalha);

      const fetchData = async (getData: (uf: string, year: number) => Promise<any>, setLoading: React.Dispatch<React.SetStateAction<boolean>>, setRawData: React.Dispatch<React.SetStateAction<any[]>>) => {
        setLoading(true);
        try {
          const promises = years.map(year => getData(uf.value, year).then(res => ({ ...res, year })));
          const results = await Promise.all(promises);
          setRawData(results);
        } catch (error) {
          console.error("Error fetching data:", error);
          setRawData([]);
        } finally {
          setLoading(false);
        }
      };

      fetchData(getDengueData, setDengueLoading, setRawDengueData);
      fetchData(getZikaData, setZikaLoading, setRawZikaData);
      fetchData(getChikungunyaData, setChikungunyaLoading, setRawChikungunyaData);

      const currentMonth = (new Date().getMonth()).toString().padStart(2, '0');
      setCnesLoading(true);

      getCnesPorNatureza(uf.value, currentYear, currentMonth)
        .then(setCnesPorNaturezaData).finally(() => setCnesLoading(false))

      getCnesEquipes(uf.value, currentYear, currentMonth)
        .then(setCnesEquipesData).finally(() => setCnesLoading(false))

    } else {
      setMunicipios([]);
      setUfMalha(null);
      setRawDengueData([]);
      setRawZikaData([]);
      setRawChikungunyaData([]);
      setDengueLoading(false);
      setZikaLoading(false);
      setChikungunyaLoading(false);
    }
  };

  const dengueChartData = useMemo(() => processData(rawDengueData, years, municipio?.id?.toString()), [rawDengueData, municipio]);
  const zikaChartData = useMemo(() => processData(rawZikaData, years, municipio?.id?.toString()), [rawZikaData, municipio]);
  const chikungunyaChartData = useMemo(() => processData(rawChikungunyaData, years, municipio?.id?.toString()), [rawChikungunyaData, municipio]);

  const selectMunicipio = (municipio: any) => {
    if (municipio) {
      getMunicipioMalha(municipio.id.toString()).then(setMunicipioMalha);
      getMunicipioInfo(municipio.id.toString()).then(setMunicipioInfo);
      getPopulationData(municipio.id.toString()).then(setPopulationData);
      getMunicipioMetadata(municipio.id.toString()).then(setMunicipioMetadata);
    } else {
      setMunicipioMalha(null);
      setMunicipioInfo(null);
    }
  };

  const [filteredUfs, setFilteredUfs] = useState<{ value: string; label: string }[]>([]);
  const [filteredMunicipios, setFilteredMunicipios] = useState<Municipio[]>([]);

  const searchUf = (event: { query: string }) => {
    let _filteredUfs;
    if (!event.query.trim()) {
      _filteredUfs = [...ufs];
    } else {
      _filteredUfs = ufs.filter((uf) =>
        uf.label.toLowerCase().startsWith(event.query.toLowerCase())
      );
    }
    setFilteredUfs(_filteredUfs);
  };

  const searchMunicipio = (event: { query: string }) => {
    let _filteredMunicipios;
    if (!event.query.trim()) {
      _filteredMunicipios = [...municipios];
    } else {
      _filteredMunicipios = municipios.filter((municipio) =>
        municipio.nome.toLowerCase().startsWith(event.query.toLowerCase())
      );
    }
    setFilteredMunicipios(_filteredMunicipios);
  };

  return (
    <div className="App">
      <div className="main-content">
        <div className="controls">
          <img style={{ marginBottom: 60, marginTop: 50 }} src={Logo} alt="Logo" width={220} />
          <div style={{ marginTop: 10 }}>
            <label>Unidade Federativa</label>
            <AutoComplete
              field="label"
              value={uf}
              suggestions={filteredUfs}
              completeMethod={searchUf}
              onChange={(e) => { setUf(e.value); setMunicipio(null) }}
              onSelect={(e) => selectUF(e.value)}
              dropdown
              placeholder="Selecione uma UF"
            />
          </div>
          <div style={{ marginTop: 10 }}>
            <label>Município</label>
            <AutoComplete
              field="nome"
              value={municipio}
              suggestions={filteredMunicipios}
              completeMethod={searchMunicipio}
              onChange={(e) => setMunicipio(e.value)}
              onSelect={(e) => selectMunicipio(e.value)}
              dropdown
              placeholder="Selecione um Município"
              disabled={!uf?.value}
            />
          </div>
          <div style={{ marginTop: 10 }}>
            <label>Zoneamento (m²)</label>
            <Dropdown
              value={zoning}
              options={zoningOptions}
              onChange={(e) => setZoning(e.value)}
              placeholder="Selecione um valor"
              disabled={!municipio?.id}
            />
          </div>
        </div>
        <div className="map-container">
          <Map stateGeoJson={ufMalha} cityGeoJson={municipioMalha} zoning={zoning} />
          {municipioInfo && (
            <div className="municipio-info-card-container">
              <Card title={municipioInfo.nome} subTitle={<div style={{ marginTop: -10 }}>{`Código IBGE: ${municipioInfo.id}`}</div>} >
                <p style={{ marginTop: -10 }}><b>UF: </b>{municipioInfo['regiao-imediata']['regiao-intermediaria'].UF.sigla}</p>
                <p style={{ marginTop: -18 }}><b>Região: </b>{municipioInfo['regiao-imediata']['regiao-intermediaria'].UF.regiao.nome}</p>
                {populationData && populationData[0] && populationData[0].resultados[0] && (
                  <p style={{ marginTop: -18 }}><b>População (2022): </b>{parseInt(populationData[0].resultados[0].series[0].serie['2022']).toLocaleString('pt-BR')}</p>
                )}
                {municipioMetadata && municipioMetadata[0].area && (
                  <p style={{ marginTop: -18, marginBottom: -10 }}><b>Área: </b>{(municipioMetadata[0].area.dimensao as number).toLocaleString('pt-BR')} km²</p>
                )}
              </Card>
            </div>
          )}
          {municipio?.id && (
            <div className="cnes-card-container">
              <Card title="Sistema Municipal de Saúde" className="card-transparent">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {cnesLoading ?
                    <ProgressSpinner />
                    :
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                        <PiHospitalFill size={50} onClick={() => setDisplayCnesPorNaturezaDialog(true)} />
                        <Badge style={{ marginLeft: -10 }} value={cnesPorNaturezaData
                          .filter((item) => item.cod_municipio === municipio.id.toString().slice(0, -1))[0]
                          ?.estabelecimentos
                          .filter((est: any) => est.tipo === 'CENTRO DE SAUDE/UNIDADE BASICA')[0]?.quantidade || 0} ></Badge>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FaUserDoctor size={42} onClick={() => setDisplayCnesEquipesDialog(true)} />
                        <Badge style={{ marginLeft: -10 }} value={cnesEquipesData
                          .filter((item) => item.cod_municipio === municipio.id.toString().slice(0, -1))[0]
                          ?.equipes
                          .filter((equipe: any) => equipe.tipo === '70 ESF - EQUIPE DE SAUDE DA FAMILIA')[0]?.quantidade || 0} ></Badge>
                      </div>
                    </>
                  }
                </div>
              </Card>
              <Dialog header="Estabelecimentos de Saúde Municipais" visible={displayCnesPorNaturezaDialog} onHide={() => setDisplayCnesPorNaturezaDialog(false)} style={{ width: '50vw' }}>
                <DataTable value={cnesPorNaturezaData.filter((item) => item.cod_municipio === municipio.id.toString().slice(0, -1)).flatMap(item => item.estabelecimentos)} size='small' paginator rows={5} filterDisplay="row" >
                  <Column field="tipo" header="Estabelecimento" filter filterMatchMode="contains" showFilterMenu={false}></Column>
                  <Column field="quantidade" header="Quantidade" filter filterMatchMode="contains" showFilterMenu={false}></Column>
                </DataTable>
              </Dialog>
              <Dialog header="Equipes de Saúde Municipais" visible={displayCnesEquipesDialog} onHide={() => setDisplayCnesEquipesDialog(false)} style={{ width: '50vw' }}>
                <DataTable value={cnesEquipesData.filter((item) => item.cod_municipio === municipio.id.toString().slice(0, -1)).flatMap(item => item.equipes)} size='small' paginator rows={5} filterDisplay="row" >
                  <Column field="tipo" header="Equipe" filter filterMatchMode="contains" showFilterMenu={false}></Column>
                  <Column field="quantidade" header="Quantidade" filter filterMatchMode="contains" showFilterMenu={false}></Column>
                </DataTable>
              </Dialog>
            </div>
          )}
          {municipio?.id && (
            <div className="charts-container">
              <Card title="Dengue" className="chart-card">
                <DiseaseChart title="Dengue" chartData={dengueChartData} loading={dengueLoading} />
              </Card>
              <Card title="Zika" className="chart-card">
                <DiseaseChart title="Zika" chartData={zikaChartData} loading={zikaLoading} />
              </Card>
              <Card title="Chikungunya" className="chart-card">
                <DiseaseChart title="Chikungunya" chartData={chikungunyaChartData} loading={chikungunyaLoading} />
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;